import { useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Package, Truck, ArrowsLeftRight, Receipt, ArrowCounterClockwise,
} from '@phosphor-icons/react';

const ICONS = {
  package: Package,
  truck: Truck,
  arrows: ArrowsLeftRight,
  receipt: Receipt,
  'return': ArrowCounterClockwise,
};

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800',
  sold: 'bg-orange-100 text-orange-800',
  in_transit: 'bg-blue-100 text-blue-800',
};

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    const dt = d.includes('T') ? new Date(d) : new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
};

export function SerialHistoryLink({ serialNumber, className = '' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data: result } = await api.get(`/serial-history/${encodeURIComponent(serialNumber)}`);
      setData(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load serial history');
      setOpen(false);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); loadHistory(); }}
        className={`inline-flex items-center text-[11px] px-2 py-1 rounded font-mono cursor-pointer hover:ring-2 hover:ring-[#0F4C81] hover:ring-offset-1 transition-all underline decoration-dotted underline-offset-2 ${className}`}
        title={`View history: ${serialNumber}`}
        data-testid={`serial-link-${serialNumber}`}
      >
        {serialNumber}
      </button>

      <Dialog open={open} onOpenChange={o => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="serial-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] text-base">
              Serial: <span className="font-mono text-[#0F4C81]">{serialNumber}</span>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
          ) : data && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#64748B]">Item</p>
                  <p className="text-sm font-semibold">{data.item_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#64748B]">Status</p>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${STATUS_COLORS[data.current_status] || 'bg-gray-100 text-gray-700'}`}>
                    {data.current_status?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="text-xs text-[#64748B]">
                Current Location: <strong className="text-[#0F172A]">{data.current_location || 'N/A'}</strong>
              </div>

              {/* Timeline */}
              <div className="relative pl-6 space-y-0">
                {data.timeline?.length > 0 ? data.timeline.map((event, idx) => {
                  const IconComp = ICONS[event.icon] || Package;
                  const isLast = idx === data.timeline.length - 1;
                  const colors = {
                    'Opening Stock Added': 'border-blue-400 bg-blue-50 text-blue-700',
                    'Purchased (Vendor Bill)': 'border-[#0F4C81] bg-[#EBF3FA] text-[#0F4C81]',
                    'Transferred': 'border-purple-400 bg-purple-50 text-purple-700',
                    'Invoiced (Sold)': 'border-orange-400 bg-orange-50 text-orange-700',
                    'Returned (Credit Note)': 'border-red-400 bg-red-50 text-red-700',
                  };
                  const cardColor = colors[event.event] || 'border-gray-300 bg-gray-50 text-gray-700';

                  return (
                    <div key={idx} className="relative pb-4">
                      {/* Vertical line */}
                      {!isLast && <div className="absolute left-[-17px] top-6 bottom-0 w-0.5 bg-[#E2E8F0]" />}
                      {/* Dot */}
                      <div className="absolute left-[-21px] top-1 w-3 h-3 rounded-full border-2 border-[#0F4C81] bg-white" />
                      {/* Card */}
                      <div className={`border rounded-lg p-3 ${cardColor}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <IconComp size={14} weight="bold" />
                          <span className="text-xs font-semibold">{event.event}</span>
                          <span className="text-[10px] ml-auto opacity-75">{fmtDate(event.date)}</span>
                        </div>
                        {event.location && <p className="text-[11px] opacity-80">Location: {event.location}</p>}
                        <p className="text-[11px]">{event.details}</p>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-[#64748B] py-4">No history events recorded.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
